import React, {useState, useRef} from 'react';
import {Form, Button, InputGroup} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faUpload, faTrashCan} from '@fortawesome/free-solid-svg-icons';
import heic2any from 'heic2any';

const FileUpload = ({onFileSelect, name, accept, placeholder = "选择文件...", required,defaultValue}) => {
    const [fileName, setFileName] = useState('');
    const fileInputRef = useRef(null);

    // 检查文件是否为需要转换的图片格式
    const needsConversion = (file) => {
        // 如果已经是PNG或JPEG格式（浏览器兼容格式），不需要转换
        if (file.type === 'image/png' || file.type === 'image/jpeg') {
            return false;
        }
        
        // 检查文件的MIME类型和扩展名
        const isHeicType = file.type === 'image/heic' || file.type === 'image/heif';
        const fileNameLower = file.name.toLowerCase();
        const hasHeicExtension = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
        
        // 对于WebP文件总是需要处理
        if (fileNameLower.endsWith('.webp')) {
            return true;
        }
        
        // 对于.HEIC/.HEIF文件扩展名，除非已经是PNG格式，否则都需要处理
        if (hasHeicExtension) {
            return file.type !== 'image/png';
        }
        
        return isHeicType;
    };

    // 将HEIC或WebP转换为PNG格式
    const convertToPng = async (file) => {
        const fileNameLower = file.name.toLowerCase();
        
        // 如果已经是PNG或JPEG格式（浏览器兼容格式），直接返回原文件
        if (file.type === 'image/png' || file.type === 'image/jpeg') {
            console.log('文件已为浏览器兼容格式，无需转换:', file.type);
            return file;
        }
        
        // 如果是HEIC/HEIF文件，进行转换
        if (fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif')) {
            // 使用heic2any转换HEIC到PNG
            try {
                const convertedBlob = await heic2any({
                    blob: file,
                    toType: "image/png",
                    quality: 0.9
                });
                
                const newName = file.name.replace(/\.heic$/i, '.png').replace(/\.heif$/i, '.png');
                const convertedFile = new File([convertedBlob], newName, { type: 'image/png' });
                console.log('HEIC文件转换成功');
                return convertedFile;
            } catch (error) {
                console.error('HEIC转换失败:', error);
                // 转换失败时返回原文件
                return file;
            }
        } else if (fileNameLower.endsWith('.webp')) {
            // 对于WebP，使用canvas进行转换
            return new Promise((resolve, reject) => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                
                img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const newName = file.name.replace(/\.webp$/i, '.png');
                            const convertedFile = new File([blob], newName, { type: 'image/png' });
                            console.log('WebP文件转换成功');
                            resolve(convertedFile);
                        } else {
                            // 转换失败时返回原文件
                            console.log('WebP文件转换失败，返回原文件');
                            resolve(file);
                        }
                    }, 'image/png');
                };
                
                img.onerror = () => {
                    // 加载失败时返回原文件
                    console.log('WebP文件加载失败，返回原文件');
                    reject(file);
                };
                
                // 读取WebP文件为DataURL
                const reader = new FileReader();
                reader.onload = (e) => {
                    img.src = e.target.result;
                };
                reader.onerror = () => {
                    // 读取失败时返回原文件
                    console.log('WebP文件读取失败，返回原文件');
                    reject(file);
                };
                reader.readAsDataURL(file);
            });
        }
        
        // 如果不是需要转换的格式，直接返回原文件
        console.log('文件格式无需转换:', file.type);
        return file;
    };

    // 检查图片是否需要压缩，最大长度为1920
    const compressImageIfNeeded = (file) => {
        return new Promise((resolve, reject) => {
            // 只处理图片文件
            if (!file.type.startsWith('image/')) {
                resolve(file);
                return;
            }

            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            img.onload = () => {
                const maxWidth = 1920;
                const maxHeight = 1920;
                let { width, height } = img;

                console.log(`原始图片尺寸: ${width}x${height}`);

                // 检查是否需要压缩
                if (width <= maxWidth && height <= maxHeight) {
                    // 不需要压缩
                    console.log('图片尺寸未超过限制，无需压缩');
                    resolve(file);
                    return;
                }

                // 计算新尺寸，保持宽高比
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                const newWidth = Math.round(width * ratio);
                const newHeight = Math.round(height * ratio);

                console.log(`压缩后图片尺寸: ${newWidth}x${newHeight}`);

                // 设置canvas尺寸
                canvas.width = newWidth;
                canvas.height = newHeight;

                // 在canvas上绘制图像
                ctx.drawImage(img, 0, 0, newWidth, newHeight);

                // 转换为blob并创建新文件
                canvas.toBlob((blob) => {
                    if (blob) {
                        // 更新文件扩展名以匹配实际格式
                        let newFileName = file.name;
                        if (file.type !== 'image/png') {
                            // 如果原文件不是JPEG格式，更新扩展名
                            if (newFileName.toLowerCase().endsWith('.heic')) {
                                newFileName = newFileName.replace(/\.heic$/i, '.png');
                            } else if (newFileName.toLowerCase().endsWith('.heif')) {
                                newFileName = newFileName.replace(/\.heif$/i, '.png');
                            } else if (newFileName.toLowerCase().endsWith('.png')) {
                                // 保持PNG扩展名
                            } else if (newFileName.toLowerCase().endsWith('.webp')) {
                                newFileName = newFileName.replace(/\.webp$/i, '.png');
                            }
                        }
                        
                        const compressedFile = new File([blob], newFileName, {
                            type: 'image/png',
                            lastModified: Date.now(),
                        });
                        
                        console.log(`图片压缩完成，文件大小从 ${file.size} bytes 压缩到 ${compressedFile.size} bytes`);
                        resolve(compressedFile);
                    } else {
                        reject(new Error('图片压缩失败'));
                    }
                }, 'image/png', 0.9);
            };

            img.onerror = () => {
                reject(new Error('无法加载图片进行压缩'));
            };

            // 读取文件为DataURL
            const reader = new FileReader();
            reader.onload = (e) => {
                img.src = e.target.result;
            };
            reader.onerror = () => {
                reject(new Error('无法读取文件'));
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (event) => {
        const files = event.target.files;
        if (files.length > 0) {
            let file = files[0];
            let displayName = file.name;
            
            // 检查是否需要转换格式（只对确实需要格式转换的文件进行转换）
            if (needsConversion(file)) {
                try {
                    file = await convertToPng(file);
                    displayName = file.name;
                } catch (error) {
                    console.error('转换失败:', error);
                    // 转换失败时保留原文件
                }
            }
            
            // 检查是否需要压缩（对所有图片文件进行检查）
            try {
                const compressedFile = await compressImageIfNeeded(file);
                file = compressedFile;
                displayName = file.name;
            } catch (error) {
                console.error('图片压缩失败:', error);
            }
            
            setFileName(displayName);
            onFileSelect && onFileSelect(file);
        } else {
            setFileName('');
            onFileSelect && onFileSelect(null);
        }
    };

    return (
        <InputGroup className="mb-3">
            <div style={{flex:'1 1 auto'}}>
                <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileChange}
                    multiple={false}
                    required={required}
                    id={name}
                    name={name}
                    style={{ opacity: '0', position: 'absolute'}}
                />
                <Form.Control
                    type="text"
                    placeholder={placeholder}
                    value={fileName}
                    readOnly
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{cursor: 'pointer'}}
                />
            </div>

            {
                fileName ? (
                    <Button
                        variant="primary"
                        style={{
                            background: 'linear-gradient(-90deg, #723BFF, #02C8E3)',
                            border: 'none'
                        }}
                        onClick={() => {
                            document.getElementById(name).value = '';
                            setFileName('');
                            onFileSelect && onFileSelect(null);
                        }}
                    >
                        <FontAwesomeIcon icon={faTrashCan} className="me-2"/>
                        清空
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        style={{
                            background: 'linear-gradient(-90deg, #723BFF, #02C8E3)',
                            border: 'none'
                        }}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        <FontAwesomeIcon icon={faUpload} className="me-2"/>
                        选择
                    </Button>
                )
            }
        </InputGroup>
    );
};

export default FileUpload;